/* import __COLOCATED_TEMPLATE__ from './teammate-assigned-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import {
  operatorDisplayName,
  shouldUseAutomationLabel,
} from 'embercom/lib/reporting/flexible/filter-display-formatter';

const UNASSIGNED_TEAMMATE_ID = '0';

export default class TeammateAssignedFilter extends Component {
  @service appService;
  @service intercomEventService;
  @service intl;
  @tracked availableTeammates = this.teammatesWithUnassigned;
  @tracked label = this.currentLabel;
  selectedDropdownItems = this.args.selected.values || [];

  get unassignedTeammateText() {
    return this.intl.t(
      'components.reporting.custom.chart-builder.filter-bar.teammate-assigned-filter.unassigned-teammate-text',
    );
  }

  get items() {
    let items = [
      {
        heading: this.intl.t(
          'components.reporting.custom.chart-builder.filter-bar.teammate-assigned-filter.heading',
        ),
        items: this.allItems,
      },
    ];

    if (shouldUseAutomationLabel(this.appService.app)) {
      let operator = this.appService.app.operatorBot;
      items.splice(0, 0, {
        heading: this.intl.t('reporting.custom-reports.operator-header'),
        items: [this._dropdownEntry(operator)],
      });
    }

    return items;
  }

  get teammatesWithUnassigned() {
    let filteredAdmins = this.appService.app.humanAdmins.filter((admin) => !admin?.is_redacted);
    return [this.appService.app.unassignedAdmin].concat(filteredAdmins);
  }

  teammateItemLabel(teammate) {
    if (teammate.id === UNASSIGNED_TEAMMATE_ID) {
      return this.unassignedTeammateText;
    } else {
      return operatorDisplayName(teammate, this.appService.app, this.intl);
    }
  }

  get allItems() {
    return this.availableTeammates.map((teammate) => this._dropdownEntry(teammate));
  }

  _dropdownEntry(teammate) {
    return {
      model: teammate,
      text: this.teammateItemLabel(teammate),
      value: teammate.id.toString(),
      component: 'reporting/flexible/filter-bar/teammate-item',
      isSelected: this.selectedDropdownItems.includes(teammate.id.toString()),
    };
  }

  get selectedTeammateIds() {
    return this.selectedDropdownItems.filter((item) => item !== REPORTING_FILTER_SELECT_ALL);
  }

  get name() {
    return this.intl.t(
      'components.reporting.custom.chart-builder.filter-bar.teammate-assigned-filter.teammate-assigned',
    );
  }

  get selectAllLabel() {
    return this.intl.t(
      'components.reporting.custom.chart-builder.filter-bar.teammate-assigned-filter.any-teammate-currently-assigned',
    );
  }

  get currentLabel() {
    if (this.selectedTeammateIds.length === 0) {
      return this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.teammate-assigned-filter.any',
      );
    }

    let operatorAndTeammates = [this.appService.app.operatorBot, ...this.availableTeammates];
    let teammateNames = this.selectedTeammateIds.map((teammateId) => {
      let teammate = operatorAndTeammates.find((t) => t?.id?.toString() === teammateId);
      return (
        operatorDisplayName(teammate, this.appService.app, this.intl) ||
        this.intl.t(
          'components.reporting.custom.chart-builder.filter-bar.teammate-assigned-filter.former-teammate',
        )
      );
    });
    return teammateNames.join(', ');
  }

  @action
  onClose() {
    if (this.isDestroying) {
      // for some reason onClose gets triggered when you remove the filter
      // even if the dropdown isn't currently open
      return;
    }

    if (!this.selectedDropdownItems.length) {
      this.selectedDropdownItems = [REPORTING_FILTER_SELECT_ALL];
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_assigned_teammate',
      object: this.args.analyticsObject,
      teammate_count: this.selectedTeammateIds.length,
    });

    this.args.setSelected(this.selectedDropdownItems);
    this.label = this.currentLabel;
  }

  @action
  removeFilter() {
    this.selectedDropdownItems = [];
    this.args.removeFilter();
  }
}
